/**
 * GameLoader.js - Progressive game loading system
 * 
 * This is the core module for the progressive loading system that creates
 * the illusion of no loading screens by:
 * 1. Loading a minimum playable area quickly
 * 2. Progressively loading more content as the player plays
 * 3. Prioritizing chunks in the player's view direction
 * 
 * Compatible with both singleplayer and editor modes.
 */

import * as THREE from 'three';
import { createLoadingStateTracker, LOADING_STATES, DEFAULT_LOADING_CONFIG } from './LoadingStates.js';
import { createChunkPrioritizer } from './ChunkPrioritizer.js';

/**
 * Create a game loader that handles progressive loading
 * @param {Object} scene - THREE.js scene
 * @param {Object} chunkManager - Terrain chunk manager
 * @param {Object} config - Configuration object
 * @returns {Object} Game loader interface
 */
export function createGameLoader(scene, chunkManager, config = {}) {
    // Merge default config with provided config
    const settings = {
        ...DEFAULT_LOADING_CONFIG,
        ...config
    };
    
    // Create loading state tracker
    const loadingState = createLoadingStateTracker((newState, oldState) => {
        // Update fog and other visual effects based on loading state
        if (newState === LOADING_STATES.TITLE_READY) {
            console.log('[GameLoader] Title screen ready');
            updateFog(settings.initialFogDensity);
        } else if (newState === LOADING_STATES.MINIMAL_LOADED) {
            console.log('[GameLoader] Minimal chunks loaded');
            updateFog(settings.initialFogDensity);
        } else if (newState === LOADING_STATES.PLAYABLE) {
            console.log('[GameLoader] Game playable');
            updateFog(settings.playableFogDensity);
        } else if (newState === LOADING_STATES.COMPLETE) {
            console.log('[GameLoader] Loading complete');
            updateFog(settings.completeFogDensity);
        }
    });
    
    // Create chunk prioritizer
    const chunkPrioritizer = createChunkPrioritizer({
        minimalChunksRadius: settings.minimalChunksRadius,
        playableChunksRadius: settings.playableChunksRadius,
        completeChunksRadius: settings.completeChunksRadius
    });
    
    // Track chunks being loaded
    const loadingQueue = new Set();
    const loadedChunks = new Set();
    
    // Track if loading is in progress
    let isLoading = false;
    let worldMode = 'dynamic'; // 'dynamic' or 'editor'
    
    /**
     * Update the fog density in the scene
     * @param {number} density - New fog density value
     */
    function updateFog(density) {
        if (scene && scene.fog) {
            scene.fog.density = density;
        }
    }
    
    /**
     * Set the world mode (dynamic or editor)
     * @param {string} mode - 'dynamic' or 'editor'
     */
    function setWorldMode(mode) {
        if (worldMode === mode) return; // No change needed
        
        console.log(`[GameLoader] Setting world mode to: ${mode}`);
        worldMode = mode;
        
        if (mode === 'editor') {
            // For editor mode, we load all chunks
            loadingState.updateState(LOADING_STATES.TITLE_READY);
            
            // Make sure the chunk manager knows we're in editor mode
            if (chunkManager && typeof chunkManager.setWorldMode === 'function') {
                chunkManager.setWorldMode('editor');
                
                // If the chunk manager has a dedicated function for editor mode, use it
                if (typeof chunkManager.loadAllChunksForEditor === 'function') {
                    console.log('[GameLoader] Using loadAllChunksForEditor for editor mode');
                    chunkManager.loadAllChunksForEditor();
                }
            }
            
            // Update loading state
            loadingState.updateState(LOADING_STATES.COMPLETE);
        } else if (mode === 'dynamic') {
            // For dynamic mode, let the chunk manager know
            if (chunkManager && typeof chunkManager.setWorldMode === 'function') {
                chunkManager.setWorldMode('dynamic');
            }
            
            // Clear our loaded chunks set to allow for dynamic loading
            loadedChunks.clear();
        }
    }
    
    /**
     * Begin loading the game (called from title screen)
     * @param {Object} spawnPosition - Player spawn position
     * @param {Function} onReadyCallback - Called when game is playable
     */
    async function beginLoading(spawnPosition, onReadyCallback) {
        if (worldMode === 'editor') {
            console.log("[GameLoader] Editor mode - skipping chunk loading");
            if (onReadyCallback) onReadyCallback();
            return;
        }

        console.log("[GameLoader] Starting progressive world loading...");
        loadingState.updateState(LOADING_STATES.INITIAL);
        
        try {
            // Calculate chunk coordinates from spawn position
            const spawnChunkX = Math.floor(spawnPosition.x / settings.chunkSize);
            const spawnChunkZ = Math.floor(spawnPosition.z / settings.chunkSize);
            
            console.log(`[GameLoader] Initial spawn chunk coordinates: ${spawnChunkX},${spawnChunkZ}`);
            
            // Create a square grid of chunks to load around spawn position
            const initialRadius = 2; // Start with a 5x5 grid (2 chunks in each direction)
            const chunksToLoad = [];
            
            // Generate chunks in a square pattern around spawn
            for (let x = -initialRadius; x <= initialRadius; x++) {
                for (let z = -initialRadius; z <= initialRadius; z++) {
                    const chunkX = spawnChunkX + x;
                    const chunkZ = spawnChunkZ + z;
                    
                    // Calculate distance from spawn for prioritization
                    const distance = Math.sqrt(x * x + z * z);
                    chunksToLoad.push({ x: chunkX, z: chunkZ, distance });
                }
            }
            
            // Sort chunks by distance from spawn
            chunksToLoad.sort((a, b) => a.distance - b.distance);
            
            // Load chunks in batches with a delay to prevent UI freezing
            const batchSize = 2;
            for (let i = 0; i < chunksToLoad.length; i += batchSize) {
                const batch = chunksToLoad.slice(i, i + batchSize);
                
                // Load each chunk in the batch
                await Promise.all(batch.map(async ({ x, z }) => {
                    await loadChunk(x, z);
                }));
                
                // Small delay between batches to prevent UI freezing
                await new Promise(resolve => setTimeout(resolve, 50));
            }
            
            // Update loading state to indicate minimal chunks are loaded
            loadingState.updateState(LOADING_STATES.MINIMAL_LOADED);
            console.log("[GameLoader] Minimal chunks loaded around spawn position");
            
            // Call the callback to spawn player
            if (onReadyCallback) {
                await onReadyCallback();
            }
            
            // Start background loading for surrounding chunks
            setTimeout(() => {
                loadSurroundingChunksAsync(spawnChunkX, spawnChunkZ, settings.playableChunksRadius);
            }, 100);
            
        } catch (error) {
            console.error("[GameLoader] Error during initial loading:", error);
            loadingState.updateState(LOADING_STATES.ERROR);
        }
    }
    
    /**
     * Load additional chunks asynchronously after initial loading
     * @param {Object} spawnPosition - Player spawn position 
     * @param {Object} tempViewDirection - View direction
     */
    async function loadMoreChunksAsync(spawnPosition, tempViewDirection) {
        try {
            const chunkSize = chunkManager.chunkSize || 32;
            
            // Continue loading remaining chunks progressively
            const playableChunks = chunkPrioritizer.prioritizeChunks(
                spawnPosition, 
                tempViewDirection, 
                { state: LOADING_STATES.MINIMAL_LOADED }, 
                chunkSize
            );
            
            // Filter out already loaded chunks
            const remainingPlayableChunks = playableChunks.filter(chunk => 
                !loadedChunks.has(`${chunk.x},${chunk.z}`)
            );
            
            // Load chunks with a small delay to avoid stuttering
            await loadChunksProgressively(remainingPlayableChunks, settings.loadingDelay || 10);
            
            // Update to PLAYABLE state
            loadingState.updateState(LOADING_STATES.PLAYABLE);
            
            // Last phase - load remaining chunks for complete state
            const completeChunks = chunkPrioritizer.prioritizeChunks(
                spawnPosition, 
                tempViewDirection, 
                { state: LOADING_STATES.PLAYABLE }, 
                chunkSize
            );
            
            const remainingCompleteChunks = completeChunks.filter(chunk => 
                !loadedChunks.has(`${chunk.x},${chunk.z}`)
            );
            
            // Load remaining chunks with longer delays
            await loadChunksProgressively(remainingCompleteChunks, 20);
            
            // Update to COMPLETE state
            loadingState.updateState(LOADING_STATES.COMPLETE);
            
            isLoading = false;
            console.log('[GameLoader] Initial loading complete');
        } catch (error) {
            console.error('[GameLoader] Error in background loading:', error);
            isLoading = false;
        }
    }
    
    /**
     * Load a single chunk and add it to the scene
     * @param {number} x - Chunk X coordinate
     * @param {number} z - Chunk Z coordinate
     */
    function loadChunk(x, z) {
        const key = `${x},${z}`;
        
        // Skip if already loaded or loading
        if (loadedChunks.has(key) || loadingQueue.has(key)) {
            return;
        }
        
        loadingQueue.add(key);
        
        try {
            // Use existing chunk loading method
            const chunk = chunkManager.getChunkAt(x, z);
            
            if (chunk?.mesh) {
                scene.add(chunk.mesh);
                chunkManager.loadedChunks.add(key);
                loadedChunks.add(key);
            }
        } catch (error) {
            console.error(`[GameLoader] Error loading chunk ${key}:`, error);
        }
        
        loadingQueue.delete(key);
    }
    
    /**
     * Load all chunks at once (used for editor mode)
     * This is a fallback if chunkManager.loadAllChunksForEditor is not available
     */
    function loadAllChunks() {
        if (!chunkManager || !chunkManager.worldSize) {
            console.error('[GameLoader] Cannot load all chunks: chunkManager not properly initialized');
            return;
        }
        
        const chunkSize = chunkManager.chunkSize || 32;
        const worldSize = chunkManager.worldSize;
        const chunksAcross = Math.ceil(worldSize / chunkSize);
        
        console.log(`[GameLoader] Loading all ${chunksAcross}x${chunksAcross} chunks for editor mode...`);
        
        // Load all chunks in a grid
        for (let x = 0; x < chunksAcross; x++) {
            for (let z = 0; z < chunksAcross; z++) {
                loadChunk(x, z);
            }
        }
        
        console.log(`[GameLoader] Loaded ${loadedChunks.size} chunks for editor mode`);
    }
    
    /**
     * Load chunks progressively with delays to prevent stuttering
     * @param {Array} chunks - Array of chunk objects with x,z coordinates
     * @param {number} delayMs - Delay between chunk loads in milliseconds
     */
    async function loadChunksProgressively(chunks, delayMs = 10) {
        const total = chunks.length;
        let loaded = 0;
        
        for (const chunk of chunks) {
            try {
                loadChunk(chunk.x, chunk.z);
                loaded++;
                
                // Update progress
                loadingState.updateProgress('chunks', loaded, total);
                
                // Add small delay to prevent freeze/stutter
                if (delayMs > 0 && loaded % 3 === 0) { // Load batches of 3
                    await new Promise(resolve => setTimeout(resolve, delayMs));
                }
            } catch (error) {
                console.error(`[GameLoader] Error in progressive loading:`, error);
            }
        }
    }
    
    /**
     * Update chunk loading based on player position while playing
     * @param {Object} playerPosition - Current player position
     * @param {Object} viewDirection - Camera direction vector
     */
    function updateChunkLoading(playerPosition, viewDirection) {
        // Skip if in editor mode
        if (worldMode === 'editor') return;
        
        // Skip if still in initial loading
        if (isLoading) return;
        
        // Get current chunk coordinates
        const chunkSize = chunkManager.chunkSize || 32;
        const centerChunkX = Math.floor(playerPosition.x / chunkSize);
        const centerChunkZ = Math.floor(playerPosition.z / chunkSize);
        
        // Get current loading radius based on state
        const radius = chunkPrioritizer.getRadiusForState(loadingState);
        
        // Create a square grid of chunks to load
        const chunksToLoad = [];
        for (let x = -radius; x <= radius; x++) {
            for (let z = -radius; z <= radius; z++) {
                const targetX = centerChunkX + x;
                const targetZ = centerChunkZ + z;
                const key = `${targetX},${targetZ}`;
                
                // Skip if already loaded or loading
                if (loadedChunks.has(key) || loadingQueue.has(key)) continue;
                
                // Calculate distance from center for prioritization
                const distance = Math.sqrt(x * x + z * z);
                
                // Add to load queue with priority
                chunksToLoad.push({
                    x: targetX,
                    z: targetZ,
                    key,
                    distance
                });
            }
        }
        
        // Sort chunks by distance (closest first)
        chunksToLoad.sort((a, b) => a.distance - b.distance);
        
        // Load up to 2 chunks per frame to avoid stuttering
        const toLoad = chunksToLoad.slice(0, 2);
        
        for (const chunk of toLoad) {
            loadChunk(chunk.x, chunk.z);
        }
    }
    
    /**
     * Get current loading state and info
     * @returns {Object} Loading state info
     */
    function getLoadingInfo() {
        return {
            state: loadingState.state,
            chunks: {
                loaded: loadingState.progress.chunks.loaded,
                total: loadingState.progress.chunks.total
            }
        };
    }
    
    /**
     * Load surrounding chunks asynchronously in a spiral pattern
     * @param {number} centerX - Center chunk X
     * @param {number} centerZ - Center chunk Z
     * @param {number} radius - Radius to load
     */
    async function loadSurroundingChunksAsync(centerX, centerZ, radius) {
        console.log(`[GameLoader] Background loading chunks in radius ${radius} around (${centerX}, ${centerZ})`);
        
        // Create spiral pattern coordinates
        const spiralCoords = [];
        let x = 0;
        let z = 0;
        let dx = 0;
        let dz = -1;
        
        // Generate spiral coordinates up to the specified radius
        for (let i = 0; i < Math.pow(2 * radius + 1, 2); i++) {
            if ((-radius <= x && x <= radius) && (-radius <= z && z <= radius)) {
                // Skip center chunk (0,0) as it's already loaded
                if (!(x === 0 && z === 0)) {
                    spiralCoords.push({ x: centerX + x, z: centerZ + z });
                }
            }
            
            // Update spiral direction
            if (x === z || (x < 0 && x === -z) || (x > 0 && x === 1 - z)) {
                const temp = dx;
                dx = -dz;
                dz = temp;
            }
            
            x += dx;
            z += dz;
        }
        
        // Load chunks with small delay between each to prevent freezing
        for (const coord of spiralCoords) {
            try {
                await loadChunk(coord.x, coord.z);
                
                // Update progress
                const loaded = loadedChunks.size;
                const total = spiralCoords.length + 1; // +1 for center chunk
                loadingState.updateProgress(loaded, total);
                
                // Small delay to prevent UI freezing
                await new Promise(resolve => setTimeout(resolve, settings.loadingDelay));
            } catch (error) {
                console.error(`[GameLoader] Error loading chunk ${coord.x},${coord.z}:`, error);
            }
        }
        
        // Update to PLAYABLE state once enough chunks are loaded
        loadingState.updateState(LOADING_STATES.PLAYABLE);
        
        // After short delay, update to COMPLETE state
        setTimeout(() => {
            loadingState.updateState(LOADING_STATES.COMPLETE);
            console.log('[GameLoader] Background chunk loading complete');
        }, 500);
    }
    
    // Public interface
    return {
        beginLoading,
        updateChunkLoading,
        getLoadingInfo,
        setWorldMode,
        get state() { return loadingState.state; },
        get isLoading() { return isLoading; }
    };
}

// MOBILE-FUTURE: Add mobile detection and configuration adjustment 